/**
 * Connection Management Service
 * Handles connection CRUD and configuration
 */

import { TeamScopedService } from '../base.service';
import type { Connection } from '../../types';

export interface ListConnectionsResponse {
  connections: Connection[];
  total?: number;
}

export interface GetConnectionResponse {
  connection: Connection;
}

export interface UpdateConnectionRequest {
  name?: string;
  active?: boolean;
  metadata?: Record<string, any>;
}

export interface UpdateCredentialsRequest {
  credentials: Record<string, any>;
}

export interface ConnectionPermissions {
  agentIds: string[];
  toolPermissions?: Record<string, 'allowed' | 'requires-approval' | 'denied'>;
}

export interface GetPermissionsResponse {
  permissions: ConnectionPermissions;
}

export interface ConnectionKnowledge {
  learningPrompt?: string;
  knowledgeContext?: string;
  lastLearned?: string;
}

export interface GetKnowledgeResponse {
  knowledge: ConnectionKnowledge;
}

export interface LearnConnectionResponse {
  jobId: string;
  status: string;
  message: string;
}

export interface TestConnectionResponse {
  success: boolean;
  message: string;
  details?: any;
}

class ConnectionManagementService extends TeamScopedService {
  /**
   * Get all connections for a team
   */
  async getAllConnections(teamId: string): Promise<Connection[]> {
    const response = await this.getTeam<ListConnectionsResponse>(teamId, '/connections');
    return response.connections || [];
  }

  /**
   * Get a specific connection by ID
   */
  async getConnection(teamId: string, connectionId: string): Promise<Connection> {
    const response = await this.getTeam<GetConnectionResponse>(
      teamId,
      `/connections/${connectionId}`
    );
    return response.connection;
  }

  /**
   * Update connection details
   */
  async updateConnection(
    teamId: string,
    connectionId: string,
    updates: UpdateConnectionRequest
  ): Promise<Connection> {
    const response = await this.patchTeam<GetConnectionResponse>(
      teamId,
      `/connections/${connectionId}`,
      updates
    );
    return response.connection;
  }

  /**
   * Delete a connection
   */
  async deleteConnection(teamId: string, connectionId: string): Promise<void> {
    await this.deleteTeam(teamId, `/connections/${connectionId}`);
  }

  /**
   * Test a connection
   */
  async testConnection(teamId: string, connectionId: string): Promise<TestConnectionResponse> {
    return this.postTeam<TestConnectionResponse>(
      teamId,
      `/connections/${connectionId}/test`,
      {}
    );
  }

  /**
   * Update connection credentials
   */
  async updateCredentials(
    teamId: string,
    connectionId: string,
    credentials: Record<string, any>
  ): Promise<void> {
    await this.patchTeam(
      teamId,
      `/connections/${connectionId}/credentials`,
      { credentials }
    );
  }

  /**
   * Get connection permissions
   */
  async getPermissions(teamId: string, connectionId: string): Promise<ConnectionPermissions> {
    const response = await this.getTeam<GetPermissionsResponse>(
      teamId,
      `/connections/${connectionId}/permissions`
    );
    return response.permissions;
  }

  /**
   * Update connection permissions
   */
  async updatePermissions(
    teamId: string,
    connectionId: string,
    permissions: ConnectionPermissions
  ): Promise<ConnectionPermissions> {
    const response = await this.patchTeam<GetPermissionsResponse>(
      teamId,
      `/connections/${connectionId}/permissions`,
      permissions
    );
    return response.permissions;
  }

  /**
   * Get connection knowledge base
   */
  async getKnowledge(teamId: string, connectionId: string): Promise<ConnectionKnowledge> {
    const response = await this.getTeam<GetKnowledgeResponse>(
      teamId,
      `/connections/${connectionId}/knowledge`
    );
    return response.knowledge;
  }

  /**
   * Update connection knowledge base
   */
  async updateKnowledge(
    teamId: string,
    connectionId: string,
    knowledge: Partial<ConnectionKnowledge>
  ): Promise<ConnectionKnowledge> {
    const response = await this.patchTeam<GetKnowledgeResponse>(
      teamId,
      `/connections/${connectionId}/knowledge`,
      knowledge
    );
    return response.knowledge;
  }

  /**
   * Trigger connection learning
   */
  async learnConnection(teamId: string, connectionId: string): Promise<LearnConnectionResponse> {
    return this.postTeam<LearnConnectionResponse>(
      teamId,
      `/connections/${connectionId}/learn`,
      {}
    );
  }
}

export const connectionManagementService = new ConnectionManagementService();
